import { useState, useCallback, useRef, useEffect } from "react";

export interface PopoverSelectOption<T> {
  value: T;
  label: string;
  description?: string;
}

export interface PopoverSelectProps<T> {
  value: T | null;
  onChange: (next: T) => void;
  options: readonly PopoverSelectOption<T>[];
  placeholder?: string;
  searchable?: boolean;
  disabled?: boolean;
  label?: string;
  triggerWidth?: number;
}

export function PopoverSelect<T extends string | number,>({
  value,
  onChange,
  options,
  placeholder = "—",
  searchable,
  disabled = false,
  label,
  triggerWidth,
}: PopoverSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlighted, setHighlighted] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const isSearchable = searchable !== undefined ? searchable : options.length > 7;

  const filtered = isSearchable && query
    ? options.filter((o) =>
        o.label.toLowerCase().includes(query.toLowerCase())
      )
    : options;

  const selectedOption = options.find((o) => o.value === value) ?? null;

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
    setHighlighted(0);
  }, []);

  const handleTriggerClick = useCallback(() => {
    setOpen((prev) => !prev);
    setQuery("");
    setHighlighted(0);
  }, []);

  const handleSelect = useCallback(
    (optValue: T) => {
      onChange(optValue);
      close();
    },
    [onChange, close],
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(e.target.value);
      setHighlighted(0);
    },
    [],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!open) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlighted((h) => Math.max(h - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const opt = filtered[highlighted];
        if (opt) handleSelect(opt.value);
      } else if (e.key === "Escape") {
        e.preventDefault();
        close();
      }
    },
    [open, filtered, highlighted, handleSelect, close],
  );

  useEffect(() => {
    if (!open) return;
    if (isSearchable && searchRef.current) {
      searchRef.current.focus();
    }
    const handleMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        close();
      }
    };
    window.addEventListener("mousedown", handleMouseDown);
    return () => {
      window.removeEventListener("mousedown", handleMouseDown);
    };
  }, [open, isSearchable, close]);

  const rootStyle: React.CSSProperties = disabled
    ? { opacity: 0.5, pointerEvents: "none", cursor: "not-allowed" }
    : {};

  const triggerStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
    padding: "6px 10px",
    border: "1px solid var(--paper-rule)",
    background: "var(--paper-bg)",
    font: "400 14px 'IBM Plex Sans', sans-serif",
    color: selectedOption ? "var(--paper-ink)" : "var(--paper-ink-2)",
    width: triggerWidth !== undefined ? `${triggerWidth}px` : undefined,
    minWidth: 0,
    position: "relative",
    boxSizing: "border-box",
  };

  const popoverStyle: React.CSSProperties = {
    position: "absolute",
    top: "100%",
    left: 0,
    minWidth: "100%",
    maxHeight: "280px",
    overflowY: "auto",
    zIndex: 1000,
    background: "var(--paper-bg)",
    border: "1px solid var(--paper-rule)",
    boxShadow: "0 12px 40px rgba(0,0,0,.18)",
  };

  return (
    <div ref={rootRef} style={{ ...rootStyle, position: "relative", display: "inline-block" }} onKeyDown={handleKeyDown}>
      {label && (
        <div
          style={{
            font: "500 10px 'JetBrains Mono', monospace",
            letterSpacing: ".16em",
            textTransform: "uppercase",
            color: "var(--paper-ink-2)",
            marginBottom: "4px",
          }}
        >
          {label}
        </div>
      )}
      <button
        type="button"
        onClick={handleTriggerClick}
        style={triggerStyle}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span style={{ flex: 1, textAlign: "left" }}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <span
          style={{
            font: "400 11px sans-serif",
            color: "var(--paper-ink-2)",
            flexShrink: 0,
          }}
        >
          ▾
        </span>
      </button>
      {open && (
        <div style={popoverStyle} role="listbox">
          {isSearchable && (
            <div style={{ padding: "6px 8px", borderBottom: "1px solid var(--paper-rule)" }}>
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={handleSearchChange}
                placeholder="Поиск..."
                style={{
                  width: "100%",
                  border: "1px solid var(--paper-rule)",
                  background: "var(--paper-bg)",
                  color: "var(--paper-ink)",
                  font: "400 13px 'IBM Plex Sans', sans-serif",
                  padding: "4px 8px",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>
          )}
          {filtered.map((opt, idx) => {
            const isSelected = opt.value === value;
            const isHighlighted = idx === highlighted;
            const optStyle: React.CSSProperties = {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              padding: "8px 10px",
              cursor: "pointer",
              font: "500 13px 'IBM Plex Sans', sans-serif",
              background: isSelected
                ? "var(--paper-ink)"
                : isHighlighted
                ? "var(--paper-dim)"
                : "transparent",
              color: isSelected ? "var(--paper-bg)" : "var(--paper-ink)",
            };
            return (
              <div
                key={String(opt.value)}
                role="option"
                aria-selected={isSelected}
                style={optStyle}
                onMouseEnter={() => setHighlighted(idx)}
                onClick={() => handleSelect(opt.value)}
              >
                <span>{opt.label}</span>
                {opt.description && (
                  <span
                    style={{
                      font: "500 9px 'JetBrains Mono', monospace",
                      letterSpacing: ".12em",
                      textTransform: "uppercase",
                      opacity: 0.65,
                      marginLeft: "8px",
                    }}
                  >
                    {opt.description}
                  </span>
                )}
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div
              style={{
                padding: "8px 10px",
                font: "400 12px 'IBM Plex Sans', sans-serif",
                color: "var(--paper-ink-2)",
                fontStyle: "italic",
              }}
            >
              Ничего не найдено
            </div>
          )}
        </div>
      )}
    </div>
  );
}
